import { useCallback, useEffect, useMemo, useReducer } from 'react'
import {
  advanceDiscreteTimeline,
  createDiscreteTimeline,
  currentFromDiscreteTimeline,
  goToDiscreteStep,
} from './discreteTimeline'

interface UseDiscreteRunnerOptions<S> {
  initialState: S
  step: (state: S) => S
  isTerminal: (state: S) => boolean
  initialSpeed?: number
  intervalMs?: (speed: number) => number
}

export interface DiscreteRunner<S> {
  current: S
  history: S[]
  index: number
  playing: boolean
  speed: number
  step: () => void
  advanceUntil: (stopWhen: (state: S) => boolean, maxSteps?: number) => void
  play: () => void
  pause: () => void
  reset: () => void
  goToStep: (n: number) => void
  setSpeed: (s: number) => void
}

interface RunnerState<S> {
  timeline: {
    history: S[]
    index: number
  }
  playing: boolean
  speed: number
}

type RunnerAction<S> =
  | {
      type: 'advance'
      stepFn: (state: S) => S
      isTerminal: (state: S) => boolean
      fromTick: boolean
    }
  | {
      type: 'reset'
      initialState: S
    }
  | {
      type: 'advanceUntil'
      stepFn: (state: S) => S
      isTerminal: (state: S) => boolean
      stopWhen: (state: S) => boolean
      maxSteps: number
    }
  | {
      type: 'goToStep'
      stepIndex: number
    }
  | {
      type: 'play'
    }
  | {
      type: 'pause'
    }
  | {
      type: 'setSpeed'
      speed: number
    }

function createInitialRunnerState<S>(
  initialState: S,
  initialSpeed: number,
): RunnerState<S> {
  return {
    timeline: createDiscreteTimeline(initialState),
    playing: false,
    speed: initialSpeed,
  }
}

function runnerReducer<S>(
  state: RunnerState<S>,
  action: RunnerAction<S>,
): RunnerState<S> {
  switch (action.type) {
    case 'advance': {
      const nextTimeline = advanceDiscreteTimeline(state.timeline, {
        step: action.stepFn,
        isTerminal: action.isTerminal,
      })

      if (nextTimeline === state.timeline) {
        if (action.fromTick && state.playing) {
          return { ...state, playing: false }
        }
        return state
      }

      return { ...state, timeline: nextTimeline }
    }
    case 'reset':
      return {
        ...state,
        playing: false,
        timeline: createDiscreteTimeline(action.initialState),
      }
    case 'advanceUntil': {
      let timeline = state.timeline
      for (let index = 0; index < action.maxSteps; index++) {
        const next = advanceDiscreteTimeline(timeline, {
          step: action.stepFn,
          isTerminal: action.isTerminal,
        })
        if (next === timeline) break
        timeline = next
        if (action.stopWhen(currentFromDiscreteTimeline(timeline))) break
      }
      return timeline === state.timeline
        ? state
        : { ...state, playing: false, timeline }
    }
    case 'goToStep': {
      const nextTimeline = goToDiscreteStep(state.timeline, action.stepIndex)
      return nextTimeline === state.timeline
        ? state
        : { ...state, playing: false, timeline: nextTimeline }
    }
    case 'play':
      return state.playing ? state : { ...state, playing: true }
    case 'pause':
      return state.playing ? { ...state, playing: false } : state
    case 'setSpeed':
      return state.speed === action.speed
        ? state
        : { ...state, speed: action.speed }
  }
}

function defaultIntervalMs(speed: number): number {
  return Math.max(200, 1200 / speed)
}

export function useDiscreteRunner<S>({
  initialState,
  step,
  isTerminal,
  initialSpeed = 1,
  intervalMs = defaultIntervalMs,
}: UseDiscreteRunnerOptions<S>): DiscreteRunner<S> {
  const [state, dispatch] = useReducer(
    runnerReducer<S>,
    createInitialRunnerState(initialState, initialSpeed),
  )

  const advanceManual = useCallback(() => {
    dispatch({
      type: 'advance',
      stepFn: step,
      isTerminal,
      fromTick: false,
    })
  }, [step, isTerminal])

  const play = useCallback(() => {
    dispatch({ type: 'play' })
  }, [])

  const advanceUntil = useCallback((stopWhen: (next: S) => boolean, maxSteps = 500) => {
    dispatch({
      type: 'advanceUntil',
      stepFn: step,
      isTerminal,
      stopWhen,
      maxSteps,
    })
  }, [step, isTerminal])

  const pause = useCallback(() => {
    dispatch({ type: 'pause' })
  }, [])

  const reset = useCallback(() => {
    dispatch({ type: 'reset', initialState })
  }, [initialState])

  const goToStep = useCallback((n: number) => {
    dispatch({ type: 'goToStep', stepIndex: n })
  }, [])

  const setSpeed = useCallback((s: number) => {
    dispatch({ type: 'setSpeed', speed: s })
  }, [])

  useEffect(() => {
    if (!state.playing) return

    const ms = intervalMs(state.speed)
    const id = setInterval(() => {
      dispatch({
        type: 'advance',
        stepFn: step,
        isTerminal,
        fromTick: true,
      })
    }, ms)

    return () => clearInterval(id)
  }, [state.playing, state.speed, intervalMs, step, isTerminal])

  const current = currentFromDiscreteTimeline(state.timeline)

  return useMemo(
    () => ({
      current,
      history: state.timeline.history,
      index: state.timeline.index,
      playing: state.playing,
      speed: state.speed,
      step: advanceManual,
      advanceUntil,
      play,
      pause,
      reset,
      goToStep,
      setSpeed,
    }),
    [
      current,
      state.timeline.history,
      state.timeline.index,
      state.playing,
      state.speed,
      advanceManual,
      advanceUntil,
      play,
      pause,
      reset,
      goToStep,
      setSpeed,
    ],
  )
}
